import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, STATUS_COLORS } from "@/lib/pharmacy";
import { reorderIntoCart } from "@/lib/reorder";
import { useCart } from "@/lib/cart";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetail() {
  const { orderNumber } = useParams();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const { addItem } = useCart();
  const nav = useNavigate();

  useEffect(() => { api.get(`/orders/by-number/${orderNumber}`).then((r) => setO(r.data)); }, [orderNumber]);

  const reorder = async () => {
    setBusy(true);
    try {
      const { added, skipped } = await reorderIntoCart(o, addItem);
      if (!added.length) return toast.error("None of these items are available anymore");
      if (skipped.length) toast.warning(`${added.length} item(s) added, ${skipped.length} skipped`);
      else toast.success(`${added.length} item(s) added to cart`);
      nav("/cart");
    } finally { setBusy(false); }
  };

  if (!o) return <div className="blz-container py-20 text-slate-500">Loading...</div>;
  const canReorder = o.status === "Delivered";

  return (
    <div className="blz-container py-10">
      <div className="text-xs text-slate-500"><Link to="/my-orders">My orders</Link> / <span className="text-slate-900 font-mono">{o.order_number}</span></div>
      <div className="flex flex-wrap justify-between items-start gap-3 mt-2">
        <div>
          <h1 className="font-display text-3xl font-bold" data-testid="od-order-number">{o.order_number}</h1>
          <div className="text-sm text-slate-500 mt-1">Placed on {o.created_at.slice(0,10)}</div>
        </div>
        <div className="flex items-center gap-3">
          {canReorder && (
            <button
              onClick={reorder}
              disabled={busy}
              className="btn-primary"
              data-testid="od-reorder"
            >
              <RotateCcw className="h-4 w-4"/> {busy ? "Adding to cart..." : "Reorder"}
            </button>
          )}
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${STATUS_COLORS[o.status]}`}>{o.status}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 card-flat p-6">
          <h3 className="font-display font-bold text-lg mb-4">Items</h3>
          <ul className="divide-y divide-slate-100">
            {o.items.map((i) => (
              <li key={`${i.product_id}-${i.pack_size}`} className="py-3 flex justify-between text-sm">
                <div><div className="font-semibold">{i.name}</div><div className="text-xs text-slate-500">{i.generic_name} · Pack of {i.pack_size} × {i.quantity}</div></div>
                <div className="font-mono">{fmtMoney(i.line_total)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Total pills</span><span className="font-mono">{o.total_pills}</span></div>
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtMoney(o.subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{fmtMoney(o.shipping_cost)}</span></div>
            <div className="flex justify-between font-bold pt-2 border-t"><span>Grand total</span><span className="font-mono">{fmtMoney(o.grand_total)}</span></div>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="card-flat p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Shipping to</div>
            <div className="text-sm">
              <div>{o.shipping.full_name}</div>
              <div className="text-slate-600">{o.shipping.address_line}</div>
              <div className="text-slate-600">{o.shipping.city}, {o.shipping.state} {o.shipping.postal_code}</div>
              <div className="text-slate-600">{o.shipping.country}</div>
              <div className="text-slate-600 mt-2">{o.shipping.phone}</div>
            </div>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Payment</div>
            <div className="text-sm font-semibold text-slate-900">{o.payment_method || "Not selected"}</div>
            <div className="text-xs text-slate-500 mt-1">Our team will share instructions by email.</div>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Status history</div>
            <ol className="space-y-2 text-sm">
              {(o.status_history || []).map((s,idx) => (
                <li key={idx} className="flex justify-between"><span>{s.status}</span><span className="text-xs text-slate-500 font-mono">{s.at.slice(0,16).replace("T"," ")}</span></li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}
